import type { Factor, FactorResult } from "@/lib/tool-kit/algebra/factor";
import {
  formatUni,
  superscript,
  type Uni,
  variableText,
} from "@/lib/tool-kit/algebra/uni";
import {
  equalsR,
  formatStyled,
  intR,
  type NumberStyle,
  negR,
  type Rational,
  writesAsFraction,
} from "@/lib/tool-kit/math/rational";

/** How a factored polynomial is written: `−2x(x − 2)(x + 2)²`. */
export interface TextOptions {
  variable: string;
  style: NumberStyle;
  mark: string;
}

function constantText(c: Rational, options: TextOptions): string {
  if (equalsR(c, intR(1))) {
    return "";
  }
  if (equalsR(c, intR(-1))) {
    return "−";
  }
  const text = formatStyled(c, options.style, options.mark);
  return writesAsFraction(c, options.style) ? `(${text})` : text;
}

export function factorText(factor: Factor, options: TextOptions): string {
  const body = formatUni(
    factor.poly,
    options.variable,
    options.style,
    options.mark
  );
  return `(${body})${factor.exponent > 1 ? superscript(factor.exponent) : ""}`;
}

/** The whole factorization as one line. */
export function formatFactorization(
  result: FactorResult,
  options: TextOptions
): string {
  const lead = `${constantText(result.constant, options)}${variableText(options.variable, result.xPower)}`;
  const factors = result.factors.map((f) => factorText(f, options)).join("");
  if (lead === "" && factors === "") {
    return "1";
  }
  if (factors === "" && (lead === "−" || equalsR(result.constant, intR(1)))) {
    return lead === "−" ? "−1" : lead;
  }
  return `${lead}${factors}`;
}

/** A polynomial in brackets when it has more than one term, as a factor is written. */
export function bracketIfSum(p: Uni, options: TextOptions): string {
  const text = formatUni(p, options.variable, options.style, options.mark);
  return p.filter((c) => c.num !== BigInt(0)).length > 1 ? `(${text})` : text;
}

export function negateText(value: Rational, options: TextOptions): string {
  return formatStyled(negR(value), options.style, options.mark);
}
